import React from 'react';

import { Badge, Group, Paper, StackProps, Text, Tooltip, createStyles } from '@mantine/core';
import { IconBuildingBank, IconEdit, IconLock, IconLockOpen, IconTrashX } from '@tabler/icons-react';
import Menu, { MenuItemProps } from 'components/Menu';
import { useAuth } from 'context/auth-context';
import { AthleteRetrieve, PaymentMethod } from 'types';

const useStyles = createStyles(theme => ({
  header: {
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'min-content auto min-content min-content',
    gridTemplateRows: 'auto',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
}));

interface IBankAccountPaymentMethodCardProps extends StackProps {
  type: PaymentMethod['type'];
  name: string;
  last4: string;
  routingNumber: string;
  isDefault: boolean;
  paymentMethodId: string;
  athlete?: AthleteRetrieve;
  openEditPaymentMethodForm: (paymentMethodId: string) => void;
  openPaymentMethodDeleteModal?: (paymentMethodId: string) => void;
  permission?: boolean;
  isLocked?: boolean;
  failureMessage?: string | null;
  handleUnlock: (paymentMethodId: string) => void;
}

const BankAccountPaymentMethodCard = ({
  type,
  name,
  last4,
  routingNumber,
  paymentMethodId,
  isDefault,
  athlete,
  openEditPaymentMethodForm,
  openPaymentMethodDeleteModal,
  handleUnlock,
  permission = true,
  isLocked = false,
  failureMessage = null,
}: IBankAccountPaymentMethodCardProps): JSX.Element => {
  const { session } = useAuth();
  const { classes, theme } = useStyles();

  const getMenuItems = (): MenuItemProps[] => {
    const actions: MenuItemProps[] = [
      {
        title: 'Edit',
        icon: <IconEdit size={theme.fontSizes.sm} />,
        onClick: () => openEditPaymentMethodForm(paymentMethodId),
      },
    ];
    if (isLocked) {
      actions.push({
        title: 'Unlock Actions',
        icon: <IconLockOpen size={theme.fontSizes.sm} />,
        onClick: () => handleUnlock(paymentMethodId),
        disabled: !permission,
      });
    }
    if (session?.role !== 'athlete') {
      actions.push({
        title: 'Remove',
        icon: <IconTrashX size={theme.fontSizes.sm} />,
        color: 'red',
        onClick: () => openPaymentMethodDeleteModal?.(paymentMethodId),
      });
    }
    return actions;
  };

  return (
    <Paper shadow='sm' radius='lg' p='md' withBorder bg={isLocked ? 'red.1' : 'white'}>
      <div className={classes.header}>
        {isLocked ? (
          <Tooltip label={failureMessage} disabled={!failureMessage}>
            <IconLock color={theme.colors.red[6]} />
          </Tooltip>
        ) : (
          <IconBuildingBank />
        )}
        <Text truncate>{name}</Text>
        {isDefault ? (
          <Badge size='sm' variant='outline' color='kiloBlue'>
            Default
          </Badge>
        ) : (
          <div />
        )}
        <Menu
          position='bottom-end'
          items={getMenuItems()}
          disabled={!permission || !!athlete?.primaryAccount}
          color={isLocked ? 'red.8' : 'kiloBlue'}
        />
      </div>
      <Group spacing='lg'>
        <Text fs='italic' c='dimmed' fz='xs' w='85px' align='right'>
          Account Number
        </Text>
        <Text fz='sm'>XXXXXX{last4}</Text>
      </Group>
      {type === 'US BANK ACCOUNT' && (
        <Group spacing='lg'>
          <Text fs='italic' c='dimmed' fz='xs' w='85px' align='right'>
            Routing Number
          </Text>
          <Text fz='sm'>{routingNumber}</Text>
        </Group>
      )}
    </Paper>
  );
};

export default BankAccountPaymentMethodCard;
